import { useContext, useEffect, useState, useRef, useCallback } from "react";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { ExperienceContext } from "../../contexts/ExperienceContext";
import { useUserContext } from "../../contexts/UserContext";
import Loader from "../UI/Loader/Loader";
import SuccessAlert from "../UI/Alert/SuccessAlert";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "../UI/ResizeablePanel/ResizeablePanel";
import { DeviceEmulator, DeviceFrameset, DeviceOptions } from "react-device-frameset";
import {
    BracesIcon,
    ChevronDown,
    CodeXmlIcon,
    EditIcon,
    FileJsonIcon,
    HelpCircle,
    Link as LinkIcon,
    ExternalLink,
} from "lucide-react";
import { Input, SegmentedControl, Select, Toggle, PrimaryButton } from "../UI";
import { permission } from "../../config/permission";
import backwardArrow from "../../assets/icons/backward-arrow.svg";
import { useNavigate } from "react-router-dom";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import { findTemplateType, isValidJsonString } from "../../utils/helper";
import EditConfigJson from "./EditConfigJson";
import { useJoyrideContext } from "../../contexts/JoyrideContext";
import { TUTORIAL_TOURS } from "../../config/tourConfig";
import Button from "../UI/Button";
import TemplateNameModal from "./TemplateNameModal";
import useComponentVisible from "../UI/ComponentVisible";
import ImportFromFigmaModal from "./ImportFromFigmaModal";
import { toast } from "react-toastify";
import { syncHTMLWithJSON } from "./helper";
import Dropdown from "../UI/Dropdown/Dropdown";
import HtmlViewerWithInspector from "./HtmlViewerWithInspector";

// Debounce utility
function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

const PreviewTemplate = ({
    previewOnly = false,
    templateName,
    templateIconURL,
    setTemplateIconURL,
    templateViewURL,
    setTemplateViewURL,
    cssCode,
    setCssCode,
    htmlCode,
    setHtmlCode,
    configJsonCode,
    setConfigJsonCode,
    jsonSchemaCode,
    setJsonSchemaCode,
    selectedTemplate,
    setTemplateName,
    validateTemplateName,
    templateError,
    submitError,
    setSubmitError = () => { },
    saveTemplateHandler,
    selectedTemplateId,
    templateType,
    templatePlatform,
    setTemplatePlatform,
    isLandscapeSupport,
    setIsLandscapeSupport,
    templateSource,
    setTemplateSource,
    apiEndPoint,
    setApiEndPoint,
    validateURL,
    isCreateTemplateFlow,
    // PreviewTitleBarEnable is a prop to enable/disable the title bar
    isPreviewTitleBarEnable,
    title,
    handleBackwardArrowClick,
    currentIndex,
    handleForwardArrowClick,
    handleProcessedClick,
    handleCancelClick,
    setTemplateType = ()=>{},
    defaultDevice,
    setDefaultDevice,
}) => {
    const navigate = useNavigate();
    const { setData, data } = useContext(ExperienceContext);
    const { checkPermission } = useUserContext();
    const { enums, content } = useContentfulContext();
    const { setState, startTour } = useJoyrideContext();
    const landscapeRef = useRef(false);
    const viewBackgroundImageRef = useRef(true);
    const [showEditJSON, setShowEditJSON] = useState(false);
    const [openNameModal, setOpenNameModal] = useState(false);
    const [codeType, setCodeType] = useState(
        templateType === enums?.templateTypes?.no_preview.id
            ? "config_json"
            : "html"
    );
    const [tourKey, setTourKey] = useState(null);
    const {
        ref: helpMenuRef,
        isComponentVisible: isHelpMenuVisible,
        setIsComponentVisible: setIsHelpMenuVisible,
    } = useComponentVisible(false);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const [loading] = useState(false);
    const [error] = useState(false);
    const [success] = useState("");

    const [openImportFromFigmaModal, setOpenImportFromFigmaModal] =
        useState(false);
    const [inspectorEnabled, setInspectorEnabled] = useState(false);

    const disabled = selectedTemplate?.scope == enums?.templateScopes.global.id;
    const disabledByPermission =
        !checkPermission(permission.TEMPLATE_CREATE) ||
        !checkPermission(permission.TEMPLATE_UPDATE);

    const formatIFrameData = ({ htmlCode, cssCode, jsonCode }) => ({
        code: {
            html: `function(template) { return \`${htmlCode}\` }`,
            styles: cssCode,
        },
        base_config_json: JSON.parse(jsonCode),
        experience_json: getExperienceJSON(JSON.parse(jsonCode)),
    });

    const getViewUrl = (isLandscape, urls) => {
        if (isLandscape) {
            return urls?.landscape_only || "";
        } else {
            return urls?.portrait_only || "";
        }
    };

    useEffect(() => {
        if (!selectedTemplate || Object.keys(selectedTemplate).length === 0)
            return;

        const formattedData = {
            ...data,
            [templateName]: formatIFrameData({
                htmlCode: htmlCode || selectedTemplate?.html,
                cssCode: cssCode || selectedTemplate?.css,
                jsonCode: configJsonCode || selectedTemplate?.config_json,
            }),
        };
        setData(formattedData);
    }, [selectedTemplate, templateType]);

    const handleTemplateChanges = (
        htmlCodeArg,
        cssCodeArg,
        configJsonCodeArg,
        jsonSchemaCodeArg,
        templateNameArg,
        templateViewURLArg,
        templateTypeArg,
        templatePlatformArg,
        templateSourceArg,
        disabledArg,
        enumsArg,
        checkPermissionArg,
        dataArg
    ) => {
        const isValidJsonSchema = isValidJsonString(
            templateSourceArg == enumsArg?.templateSources.api.id
                ? "{}"
                : jsonSchemaCodeArg
        );
        if (!isValidJsonSchema) {
            setSubmitError(content.MANAGE_TEMPLATE_CONTENT.invalid_json_schema);
            return;
        }

        const isValidJsonConfigJson = isValidJsonString(configJsonCodeArg);
        if (!isValidJsonConfigJson) {
            setSubmitError(content.MANAGE_TEMPLATE_CONTENT.invalid_config_json);
            return;
        }
        setSubmitError("");

        const newData = { ...dataArg };
        newData[templateNameArg] = formatIFrameData({
            htmlCode: htmlCodeArg,
            cssCode: cssCodeArg,
            jsonCode: configJsonCodeArg,
        });
        setData(newData);

        const detectTemplateType = findTemplateType(enumsArg, {
            html: htmlCodeArg,
            css: cssCodeArg,
            config_json: configJsonCodeArg,
            json_schema: jsonSchemaCodeArg || "{}",
        });

        setTemplateType(detectTemplateType);

        setShowEditJSON(false);
    };

    // Store latest values in a ref for debounce
    const latestValuesRef = useRef({});
    useEffect(() => {
        latestValuesRef.current = {
            htmlCode,
            cssCode,
            configJsonCode,
            jsonSchemaCode,
            templateName,
            templateViewURL,
            templateType,
            templatePlatform,
            templateSource,
            disabled,
            enums,
            checkPermission,
        };
    }, [
        htmlCode,
        cssCode,
        configJsonCode,
        jsonSchemaCode,
        templateName,
        templateViewURL,
        templateType,
        templatePlatform,
        templateSource,
        disabled,
        enums,
        checkPermission
    ]);

    // Debounced version of handleTemplateChanges (created only once)
    const debouncedTemplateChange = useCallback(
        debounce(() => {
            const v = latestValuesRef.current;
            if (
                !previewOnly &&
                !v.disabled &&
                v.checkPermission(permission.TEMPLATE_OP_EDIT_PREVIEW) &&
                v.templateType !== v.enums?.templateTypes.no_preview.id
            ) {
                handleTemplateChanges(
                    v.htmlCode,
                    v.cssCode,
                    v.configJsonCode,
                    v.jsonSchemaCode,
                    v.templateName,
                    v.templateViewURL,
                    v.templateType,
                    v.templatePlatform,
                    v.templateSource,
                    v.disabled,
                    v.enums,
                    v.checkPermission,
                    data
                );
            }
        }, 400),
        []
    );

    // Live update preview on template field changes
    useEffect(() => {
        debouncedTemplateChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        htmlCode,
        cssCode,
        configJsonCode,
        jsonSchemaCode,
        templateName,
        templateViewURL,
        templateType,
        templatePlatform,
        templateSource,
        disabled,
        enums,
        checkPermission
    ]);

    const toggleInspector = () => {
        const newInspectorState = !inspectorEnabled;
        setInspectorEnabled(newInspectorState);
    };

    const handleElementUpdate = ({ type, dztId, id, content, rawHtml, rawCss }) => {
        // dzt-id click (inspector off): scroll to the matching config form field
        // This mirrors preview.html's sendMessageToParent(dztId) → parent scroll behavior
        if (type === 'dzt-id-click' && dztId) {
            // Try to find the matching config field element by exp_json_field
            // Config form fields get their id from the exp_json_field value
            const fieldEl = document.getElementById(`field-${dztId}`) ||
                document.querySelector(`[data-field-id="${dztId}"]`) ||
                document.querySelector(`[name="${dztId}"]`);
            if (fieldEl) {
                fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Flash highlight to draw attention
                const original = fieldEl.style.outline;
                fieldEl.style.outline = '2px solid #007bff';
                fieldEl.style.transition = 'outline 0.3s';
                setTimeout(() => { fieldEl.style.outline = original; }, 1500);
            }
            return;
        }

        // element-updated (inspector modal save): update raw html/css in editors
        if (typeof rawHtml === "string") {
            setHtmlCode(rawHtml);
        }
        if (typeof rawCss === "string" && rawCss.length) {
            setCssCode(rawCss);
        }
    };

    const getExperienceJSON = (base_config_json) => {
        const experience_json = {};
        base_config_json.forEach((item) => {
            experience_json[item.id] = item.input.input_default_value;
        });
        return transformExperienceJSON(experience_json);
    };

    function transformExperienceJSON(json) {
        const transformedJSON = {};
        for (const key in json) {
            const keys = key.split(".");
            keys.reduce((acc, part, index) => {
                if (part.includes("[")) {
                    const [arrayKey, arrayIndex] = part
                        .split(/\[|\]/)
                        .filter(Boolean);
                    if (!acc[arrayKey]) {
                        acc[arrayKey] = [];
                    }
                    if (!acc[arrayKey][arrayIndex]) {
                        acc[arrayKey][arrayIndex] = {};
                    }
                    if (index === keys.length - 1) {
                        acc[arrayKey][arrayIndex] = json[key];
                    }
                    return acc[arrayKey][arrayIndex];
                } else {
                    if (index === keys.length - 1) {
                        acc[part] = json[key];
                    } else {
                        if (!acc[part]) {
                            acc[part] = {};
                        }
                        return acc[part];
                    }
                }
            }, transformedJSON);
        }
        return transformedJSON;
    }

    const triggerTour = (tourKey) => {
        const tourConfig = TUTORIAL_TOURS(content.TOURS)[tourKey];
        if (!tourConfig) return;

        // Filter steps dynamically based on current templatePlatform and templateSource
        const filteredSteps = tourConfig.steps.filter((step) => {
            if (step.hideStep) {
                return !(
                    step.hideStep.includes(templatePlatform) ||
                    step.hideStep.includes(templateSource)
                );
            }
            return true;
        });

        const updateAndStartTour = () => {
            setState((prev) => ({ ...prev, steps: filteredSteps }));
            startTour(tourConfig.id);
        };

        if (tourConfig.initialRoute) {
            navigate(tourConfig.initialRoute);
            setTimeout(updateAndStartTour, 500); // Delay tour start to allow page to render
        } else {
            updateAndStartTour();
        }
    };

    useEffect(() => {
        if (templatePlatform && templateSource) {
            const tourConfig = TUTORIAL_TOURS(content.TOURS)[
                tourKey || "CREATE_TEMPLATE_TOUR"
            ];
            const filteredSteps = tourConfig.steps.filter((step) => {
                if (step.hideStep) {
                    // If the platform or source matches any value in hideStep, we filter out the step
                    if (
                        step.hideStep.includes(templatePlatform) ||
                        step.hideStep.includes(templateSource)
                    ) {
                        return false; // Hide this step
                    }
                }
                return true; // Show this step
            });
            setState((prev) => ({ ...prev, steps: filteredSteps }));
        }
    }, [templatePlatform, templateSource]);



    const saveTemplate = () => {
        if (isCreateTemplateFlow) {
            setOpenNameModal(true);
        } else {
            saveTemplateHandler();
        }
    };
    // Radio button options
    const platformOptions = enums?.templatePlatformArray || [];
    const sourceOptions = checkPermission(
        permission.TEMPLATE_OP_CREATE_SOURCE_API
    )
        ? enums?.templateSourcesArray || []
        : (enums?.templateSourcesArray || []).filter(
              (x) => enums?.templateSources.api.id != x.id
          );

    // function syncHTMLWithJSON(htmlString, jsonArray) {
    //     console.log(htmlString);
    //     if (!htmlString || !Array.isArray(jsonArray)) return htmlString;

    //     let updatedHTML = htmlString;

    //     console.log("hit");
    //     jsonArray.forEach((item) => {
    //         const field = item?.exp_json_field;
    //         if (!field) return;

    //         const parts = field.split(".");
    //         const propName = parts.pop(); // e.g., time
    //         const templateValue = `"${"${template." + field + "}"}"`; // -> "${template.initialDelay.time}"

    //         // Match pattern like: time: "anything" OR "time": "anything"
    //         const regex = new RegExp(
    //             `(["']?${propName}["']?\\s*:\\s*)"(.*?)"`,
    //             "g"
    //         );

    //         updatedHTML = updatedHTML.replace(regex, `$1${templateValue}`);
    //     });

    //     return updatedHTML;
    // }

    const handleSyncHTMLtoJsonConfigHTML = () => {
        if (templateType !== "json_a_to_html") return;

        const result = syncHTMLWithJSON(
            htmlCode,
            configJsonCode,
            JSON.parse(jsonSchemaCode).config_obj_key || null
        );
        setHtmlCode(result);
    };

    return (
        <div className="w-full h-full flex flex-col ">
            {success && (
                <SuccessAlert disappearTime={5000}>
                    {content.CREATE_EXPERIENCE.success}
                </SuccessAlert>
            )}
            {loading && <Loader />}

            {!previewOnly && (
                <div className="sticky right-0 left-0 z-30 bg-white pt-3 pb-2 mb-1 w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <a
                        href="#"
                        className="font-medium text-primary hover:underline flex items-center"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate("/experience/manage-templates");
                        }}
                    >
                        <img
                            src={backwardArrow}
                            alt=""
                            className="w-5 h-5 mr-2"
                        />{" "}
                        {content.MANAGE_TEMPLATE_CONTENT.back_cta_text}
                    </a>
                    {
                        <div className="flex gap-3 justify-end min-w-max items-center">
                            {/* Help Button with Menu */}
                            <div className="relative">
                                <button
                                    ref={helpMenuRef}
                                    onClick={() =>
                                        setIsHelpMenuVisible(!isHelpMenuVisible)
                                    }
                                    className="flex items-center justify-center rounded hover:bg-muted transition p-1"
                                    title="Help"
                                >
                                    <HelpCircle size={22} />
                                </button>

                                {isHelpMenuVisible && (
                                    <div
                                        ref={helpMenuRef}
                                        className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                                    >
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setTourKey(
                                                    "CREATE_TEMPLATE_HELP_TOUR"
                                                );
                                                triggerTour(
                                                    "CREATE_TEMPLATE_HELP_TOUR"
                                                );
                                            }}
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            {
                                                content.MANAGE_TEMPLATE_CONTENT
                                                    .help_text.start_tour
                                            }
                                        </button>
                                        <button
                                            onClick={() =>
                                                window.open(
                                                    `${content.MANAGE_TEMPLATE_CONTENT.document_url}`,
                                                    "_blank"
                                                )
                                            }
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            {
                                                content.MANAGE_TEMPLATE_CONTENT
                                                    .help_text.document
                                            }
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Apply Changes Button */}
                            {/* {!disabled &&
                                checkPermission(
                                    permission.TEMPLATE_OP_EDIT_PREVIEW
                                ) &&
                                templateType !==
                                    enums?.templateTypes.no_preview.id && (
                                    <button
                                        onClick={() => handleTemplateChanges()}
                                        className="rounded hover:bg-muted transition"
                                        id="configure-template-apply-id"
                                        title={content.EDIT_EXPERIENCE.save_btn}
                                    >
                                        <Eye size={22} />
                                    </button>
                                )} */}

                            {/* Inspector Toggle Button */}
                            {!disabled &&
                                templateType !==
                                    enums?.templateTypes.no_preview.id && (
                                    <button
                                        onClick={toggleInspector}
                                        className={`rounded hover:bg-muted transition ${
                                            inspectorEnabled
                                                ? "bg-primary text-primary"
                                                : ""
                                        }`}
                                        title={
                                            inspectorEnabled
                                                ? "Disable Element Inspector"
                                                : "Enable Element Inspector"
                                        }
                                    >
                                        <EditIcon size={22} />
                                    </button>
                                )}

                            <div className="relative w-full">
                                {selectedTemplate?.scope !==
                                    enums?.templateScopes.global.id &&
                                    (checkPermission(
                                        permission.TEMPLATE_CREATE
                                    ) ||
                                        checkPermission(
                                            permission.TEMPLATE_UPDATE
                                        )) && (
                                        <PrimaryButton
                                            id="configure-template-save-id"
                                            className="!font-bold w-full md:w-auto"
                                            handleClick={saveTemplate}
                                            size="xs"
                                            disable={
                                                selectedTemplate.scope ===
                                                "global"
                                            }
                                        >
                                            {/* {content.MANAGE_TEMPLATE_CONTENT.save} */}{" "}
                                            Save
                                        </PrimaryButton>
                                    )}
                            </div>
                        </div>
                    }
                </div>
            )}

            {/* Main Content Area */}
            <div className="flex-grow overflow-auto h-[calc(100vh-55px)]">
                <ResizablePanelGroup direction={isMobile ? "vertical" : "horizontal"}>
                    {!previewOnly && (
                        <ResizablePanel
                            defaultSize={30}
                            minSize={20}
                            className="h-full"
                        >
                            <div className="border-r border-stride-border pr-5 h-full overflow-y-auto">
                                <div className="space-y-4">
                                    {/* Template Name Input */}
                                    {!isCreateTemplateFlow && (
                                        <div className="w-full">
                                            <Input
                                                id="template_name"
                                                name="template_name"
                                                label={content.MANAGE_TEMPLATE_CONTENT.form.name}
                                                placeholder={content.MANAGE_TEMPLATE_CONTENT.form.name}
                                                disabled={selectedTemplate.scope === "global"}
                                                value={templateName}
                                                onChange={(e) => setTemplateName(e.target.value)}
                                            />
                                        </div>
                                    )}

                                    {/* Default Device Dropdown */}
                                    <div className="w-full">
                                        <label className="block text-xs font-semibold uppercase tracking-wider text-stride-muted mb-1.5">
                                            Default Device
                                        </label>
                                        <Select
                                            id="default-device-dropdown"
                                            value={defaultDevice}
                                            onChange={(e) => setDefaultDevice(e.target.value)}
                                            options={Object.keys(DeviceOptions).map((key) => ({ value: key, label: key }))}
                                            placeholder="Select default device"
                                        />
                                    </div>

                                    {/* Platform Selection */}
                                    <div className="w-full">
                                        <label className="block text-xs font-semibold uppercase tracking-wider text-stride-muted mb-1.5">
                                            {content.MANAGE_TEMPLATE_CONTENT.platform}
                                        </label>

                                        <div className="flex flex-col gap-3">
                                            {/* Platform Buttons */}
                                            <SegmentedControl
                                                id="template-field-platform"
                                                className="max-w-fit"
                                                value={templatePlatform}
                                                onChange={setTemplatePlatform}
                                                options={platformOptions.map((opt) => ({
                                                    value: opt.id,
                                                    label: opt.name,
                                                }))}
                                                size="md"
                                            />

                                            {/* 🔽 Web Icon URL */}
                                            {(templatePlatform === enums?.templatePlatforms.web_only.id ||
                                                templatePlatform === enums?.templatePlatforms.responsive.id) && (
                                                <div className="w-full">
                                                    <Input
                                                        id="template_icon_url_web_only"
                                                        name="template_icon_url_web_only"
                                                        label={content.MANAGE_TEMPLATE_CONTENT.form.icon_url_web_only}
                                                        placeholder="https://..."
                                                        disabled={selectedTemplate.scope === "global"}
                                                        value={templateIconURL.web_only}
                                                        prefixIcon={<LinkIcon size={14} className="text-stride-muted" />}
                                                        suffixIcon={
                                                            templateIconURL.web_only ? (
                                                                <a
                                                                    href={templateIconURL.web_only}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="text-primary hover:text-primary-hover p-0.5 inline-flex items-center"
                                                                    title="Open URL"
                                                                >
                                                                    <ExternalLink size={13} />
                                                                </a>
                                                            ) : null
                                                        }
                                                        onChange={(e) =>
                                                            setTemplateIconURL((prev) => ({
                                                                ...prev,
                                                                web_only: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                </div>
                                            )}

                                            {/* 🔽 Mobile Icon */}
                                            {(templatePlatform === enums?.templatePlatforms.mobile_only.id ||
                                                templatePlatform === enums?.templatePlatforms.responsive.id) && (
                                                <div className="w-full">
                                                    <Input
                                                        id="template_icon_url_mobile_only"
                                                        name="template_icon_url_mobile_only"
                                                        label={content.MANAGE_TEMPLATE_CONTENT.form.icon_url_mobile_only}
                                                        placeholder="https://..."
                                                        disabled={selectedTemplate.scope === "global"}
                                                        value={templateIconURL.mobile_only}
                                                        prefixIcon={<LinkIcon size={14} className="text-stride-muted" />}
                                                        suffixIcon={
                                                            templateIconURL.mobile_only ? (
                                                                <a
                                                                    href={templateIconURL.mobile_only}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="text-primary hover:text-primary-hover p-0.5 inline-flex items-center"
                                                                    title="Open URL"
                                                                >
                                                                    <ExternalLink size={13} />
                                                                </a>
                                                            ) : null
                                                        }
                                                        onChange={(e) =>
                                                            setTemplateIconURL((prev) => ({
                                                                ...prev,
                                                                mobile_only: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                </div>
                                            )}

                                            {/* View URLs  */}
                                            {/* Mobile Portrait View URL (optional) */}
                                            <div className="w-full">
                                                <Input
                                                    id="template_view_url_mobile_portrait"
                                                    name="template_view_url_mobile_portrait"
                                                    label={
                                                        templatePlatform !== enums?.templatePlatforms?.web_only?.id
                                                            ? content.MANAGE_TEMPLATE_CONTENT.form.view_url_mobile_portrait
                                                            : "Background Image URL"
                                                    }
                                                    placeholder="https://..."
                                                    disabled={selectedTemplate.scope === "global"}
                                                    value={templateViewURL.portrait_only}
                                                    prefixIcon={<LinkIcon size={14} className="text-stride-muted" />}
                                                    suffixIcon={
                                                        templateViewURL.portrait_only ? (
                                                            <a
                                                                href={templateViewURL.portrait_only}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-primary hover:text-primary-hover p-0.5 inline-flex items-center"
                                                                title="Open URL"
                                                            >
                                                                <ExternalLink size={13} />
                                                            </a>
                                                        ) : null
                                                    }
                                                    onChange={(e) =>
                                                        setTemplateViewURL((prev) => ({
                                                            ...prev,
                                                            portrait_only: e.target.value,
                                                        }))
                                                    }
                                                />
                                            </div>

                                            {/* Mobile Landscape View URL (optional) */}
                                            {templatePlatform !== enums?.templatePlatforms?.web_only?.id && (
                                                <div className="max-w-md w-full">
                                                    <Input
                                                        id="template_view_url_mobile_landscape"
                                                        name="template_view_url_mobile_landscape"
                                                        label={content.MANAGE_TEMPLATE_CONTENT.form.view_url_mobile_landscape}
                                                        placeholder="https://..."
                                                        disabled={selectedTemplate.scope === "global"}
                                                        value={templateViewURL.landscape_only}
                                                        prefixIcon={<LinkIcon size={14} className="text-stride-muted" />}
                                                        suffixIcon={
                                                            templateViewURL.landscape_only ? (
                                                                <a
                                                                    href={templateViewURL.landscape_only}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="text-primary hover:text-primary-hover p-0.5 inline-flex items-center"
                                                                    title="Open URL"
                                                                >
                                                                    <ExternalLink size={13} />
                                                                </a>
                                                            ) : null
                                                        }
                                                        onChange={(e) =>
                                                            setTemplateViewURL((prev) => ({
                                                                ...prev,
                                                                landscape_only: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Source Selection */}
                                    <div className="w-full">
                                        <label className="text-xs font-semibold text-stride-muted uppercase tracking-wider mb-2 block">
                                            {content.MANAGE_TEMPLATE_CONTENT.source}
                                        </label>
                                        <div className="flex flex-col gap-2">
                                            <SegmentedControl
                                                id="template-field-source"
                                                className="max-w-fit"
                                                value={templateSource}
                                                onChange={setTemplateSource}
                                                disabled={
                                                    selectedTemplate?.scope == enums?.templateScopes.global.id ||
                                                    disabledByPermission
                                                }
                                                options={sourceOptions.map((opt) => ({
                                                    value: opt.id,
                                                    label: opt.name,
                                                }))}
                                                size="md"
                                            />
                                            {templateSource == enums?.templateSources.api.id && (
                                                <div className="max-w-md w-full mt-2">
                                                    <Input
                                                        id="api_end_point"
                                                        name="api_end_point"
                                                        label={`${selectedTemplateId ? "Update" : "Enter"} API Endpoint`}
                                                        mandatory
                                                        placeholder="https://api.example.com/endpoint"
                                                        value={apiEndPoint}
                                                        prefixIcon={<LinkIcon size={14} className="text-stride-muted" />}
                                                        suffixIcon={
                                                            apiEndPoint ? (
                                                                <a
                                                                    href={apiEndPoint}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="text-primary hover:text-primary-hover p-0.5 inline-flex items-center"
                                                                    title="Open URL"
                                                                >
                                                                    <ExternalLink size={13} />
                                                                </a>
                                                            ) : null
                                                        }
                                                        onChange={(e) => {
                                                            setApiEndPoint(e.target.value);
                                                        }}
                                                        onBlur={validateURL}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Import */}
                                    {templatePlatform !== enums?.templatePlatforms?.responsive?.id && (
                                        <div className="w-full">
                                            <label className="text-xs font-semibold text-stride-muted uppercase tracking-wider mb-2 block">
                                                {"Import"}
                                            </label>
                                            <div className="flex flex-col gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-fit"
                                                    disabled={selectedTemplate.scope === "global"}
                                                    onClick={() => setOpenImportFromFigmaModal(true)}
                                                >
                                                    Import from Figma
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {templatePlatform !== "web_only" && (
                                        <div
                                            className="w-full flex items-center justify-between py-2 border-t border-stride-border"
                                            id="template-field-landscape"
                                        >
                                            <label className="text-xs font-semibold text-stride-muted uppercase tracking-wider block">
                                                {content.MANAGE_TEMPLATE_CONTENT.landscape_support}
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <Toggle
                                                    id="template-field-landscape-toggle"
                                                    checked={isLandscapeSupport}
                                                    onChange={(e) =>
                                                        setIsLandscapeSupport(e.target.checked)
                                                    }
                                                    size="sm"
                                                />
                                                <span className="text-xs font-semibold text-stride-heading">
                                                    {isLandscapeSupport ? "ON" : "OFF"}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {(templateError || submitError) && (
                                        <p className="mt-2 text-sm text-red-5">
                                            {templateError || submitError}
                                        </p>
                                    )}

                                    {/* Code Editor Tabs */}
                                    <div className="mt-4">
                                        <div className="w-full items-center py-1 flex-wrap gap-4 bg-white">
                                            <div
                                                id="configure-template-form-id"
                                                className="flex flex-col -mb-px text-sm font-medium"
                                            >
                                                {/* HTML Tab */}
                                                <div
                                                    id="template-html"
                                                    className="border border-border rounded-xl overflow-hidden bg-white shadow-xs transition-all mb-2.5"
                                                >
                                                    <button
                                                        type="button"
                                                        disabled={
                                                            templateType ===
                                                            enums?.templateTypes
                                                                .no_preview.id
                                                        }
                                                        className={`flex items-center justify-between w-full px-3.5 py-2.5 text-xs font-semibold transition-colors duration-150 ${
                                                            templateType ===
                                                            enums?.templateTypes
                                                                .no_preview.id
                                                                ? "bg-surface-subtle text-stride-muted cursor-not-allowed opacity-50"
                                                                : codeType === "html"
                                                                ? "bg-primary-soft/50 text-primary border-b border-border"
                                                                : "bg-white text-stride-heading hover:bg-surface-subtle"
                                                        }`}
                                                        onClick={() => {
                                                            if (
                                                                templateType ===
                                                                enums
                                                                    ?.templateTypes
                                                                    .no_preview
                                                                    .id
                                                            )
                                                                return;
                                                            setCodeType(
                                                                (prev) =>
                                                                    prev !==
                                                                    "html"
                                                                        ? "html"
                                                                        : ""
                                                            );
                                                        }}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <CodeXmlIcon className="w-4 h-4 text-primary shrink-0" />
                                                            <span>
                                                                {
                                                                    content
                                                                        .EDIT_EXPERIENCE
                                                                        .html_tab
                                                                }
                                                            </span>
                                                            <span className="text-[10px] font-mono font-medium px-1.5 py-0.5 rounded bg-surface border border-border text-stride-muted uppercase">
                                                                HTML
                                                            </span>
                                                        </span>
                                                        <ChevronDown
                                                            className={`w-4 h-4 transition-transform duration-200 shrink-0 ${
                                                                codeType === "html"
                                                                    ? "rotate-180 text-primary"
                                                                    : "text-stride-muted"
                                                            }`}
                                                        />
                                                    </button>
                                                    {codeType === "html" && (
                                                        <div className="overflow-auto text-wrap">
                                                            <CodeMirror
                                                                value={htmlCode}
                                                                onChange={(
                                                                    code
                                                                ) => {
                                                                    setHtmlCode(
                                                                        code
                                                                    );
                                                                }}
                                                                readOnly={
                                                                    disabled ||
                                                                    templateType ===
                                                                        enums
                                                                            ?.templateTypes
                                                                            .no_preview
                                                                            .id
                                                                }
                                                                editable={
                                                                    !disabled &&
                                                                    templateType !==
                                                                        enums
                                                                            ?.templateTypes
                                                                            .no_preview
                                                                            .id
                                                                }
                                                                height="calc(100vh - 500px)"
                                                                width="100%"
                                                                lineWrapping={
                                                                    true
                                                                }
                                                                lineNumbers={
                                                                    true
                                                                }
                                                                extensions={[
                                                                    EditorView.lineWrapping,
                                                                ]}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* CSS Tab */}
                                                <div
                                                    id="template-css"
                                                    className="border border-border rounded-xl overflow-hidden bg-white shadow-xs transition-all mb-2.5"
                                                >
                                                    <button
                                                        type="button"
                                                        disabled={
                                                            templateType ===
                                                            enums?.templateTypes
                                                                .no_preview.id
                                                        }
                                                        className={`flex items-center justify-between w-full px-3.5 py-2.5 text-xs font-semibold transition-colors duration-150 ${
                                                            templateType ===
                                                            enums?.templateTypes
                                                                .no_preview.id
                                                                ? "bg-surface-subtle text-stride-muted cursor-not-allowed opacity-50"
                                                                : codeType === "css"
                                                                ? "bg-primary-soft/50 text-primary border-b border-border"
                                                                : "bg-white text-stride-heading hover:bg-surface-subtle"
                                                        }`}
                                                        onClick={() => {
                                                            if (
                                                                templateType ===
                                                                enums
                                                                    ?.templateTypes
                                                                    .no_preview
                                                                    .id
                                                            )
                                                                return;
                                                            setCodeType(
                                                                (prev) =>
                                                                    prev !==
                                                                    "css"
                                                                        ? "css"
                                                                        : ""
                                                            );
                                                        }}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <BracesIcon className="w-4 h-4 text-primary shrink-0" />
                                                            <span>
                                                                {
                                                                    content
                                                                        .EDIT_EXPERIENCE
                                                                        .css_tab
                                                                }
                                                            </span>
                                                            <span className="text-[10px] font-mono font-medium px-1.5 py-0.5 rounded bg-surface border border-border text-stride-muted uppercase">
                                                                CSS
                                                            </span>
                                                        </span>
                                                        <ChevronDown
                                                            className={`w-4 h-4 transition-transform duration-200 shrink-0 ${
                                                                codeType === "css"
                                                                    ? "rotate-180 text-primary"
                                                                    : "text-stride-muted"
                                                            }`}
                                                        />
                                                    </button>
                                                    {codeType === "css" && (
                                                        <div className="overflow-auto text-wrap">
                                                            <CodeMirror
                                                                value={cssCode}
                                                                onChange={(
                                                                    code
                                                                ) => {
                                                                    setCssCode(
                                                                        code
                                                                    );
                                                                }}
                                                                readOnly={
                                                                    disabled ||
                                                                    templateType ===
                                                                        enums
                                                                            ?.templateTypes
                                                                            .no_preview
                                                                            .id
                                                                }
                                                                editable={
                                                                    !disabled &&
                                                                    templateType !==
                                                                        enums
                                                                            ?.templateTypes
                                                                            .no_preview
                                                                            .id
                                                                }
                                                                height="calc(100vh - 500px)"
                                                                width="100%"
                                                                lineWrapping={
                                                                    true
                                                                }
                                                                lineNumbers={
                                                                    true
                                                                }
                                                                extensions={[
                                                                    EditorView.lineWrapping,
                                                                ]}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Config JSON Tab */}
                                                <div
                                                    id="template-config-json"
                                                    className="border border-border rounded-xl overflow-hidden bg-white shadow-xs transition-all mb-2.5"
                                                >
                                                    <button
                                                        type="button"
                                                        className={`flex items-center justify-between w-full px-3.5 py-2.5 text-xs font-semibold transition-colors duration-150 ${
                                                            codeType === "config_json"
                                                                ? "bg-primary-soft/50 text-primary border-b border-border"
                                                                : "bg-white text-stride-heading hover:bg-surface-subtle"
                                                        }`}
                                                        onClick={() => {
                                                            setCodeType((prev) =>
                                                                prev !== "config_json" ? "config_json" : ""
                                                            );
                                                        }}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <FileJsonIcon className="w-4 h-4 text-primary shrink-0" />
                                                            <span>
                                                                {
                                                                    content
                                                                        .EDIT_EXPERIENCE
                                                                        .config_json_tab
                                                                }
                                                            </span>
                                                            <span className="text-[10px] font-mono font-medium px-1.5 py-0.5 rounded bg-surface border border-border text-stride-muted uppercase">
                                                                JSON
                                                            </span>
                                                            <button
                                                                type="button"
                                                                id="template-config-json-edit-icon"
                                                                title="Edit in Visual Grid"
                                                                className="ml-1 p-1 rounded-md text-stride-muted hover:text-primary hover:bg-white border border-transparent hover:border-border transition-colors inline-flex items-center justify-center"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowEditJSON(
                                                                        !showEditJSON
                                                                    );
                                                                }}
                                                            >
                                                                <EditIcon className="w-3.5 h-3.5" />
                                                            </button>
                                                        </span>
                                                        <ChevronDown
                                                            className={`w-4 h-4 transition-transform duration-200 shrink-0 ${
                                                                codeType === "config_json"
                                                                    ? "rotate-180 text-primary"
                                                                    : "text-stride-muted"
                                                            }`}
                                                        />
                                                    </button>
                                                    {showEditJSON && (
                                                        <EditConfigJson
                                                            configJsonCode={
                                                                configJsonCode
                                                            }
                                                            jsonSchemaCode={jsonSchemaCode
                                                                .replaceAll(
                                                                    "&lt;",
                                                                    "<"
                                                                )
                                                                ?.replaceAll(
                                                                    "&gt;",
                                                                    ">"
                                                                )}
                                                            setConfigJsonCode={
                                                                setConfigJsonCode
                                                            }
                                                            closeEditConfigJsonModal={() =>
                                                                setShowEditJSON(
                                                                    false
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    {codeType === "config_json" && (
                                                        <div className="overflow-auto text-wrap">
                                                            <CodeMirror
                                                                value={
                                                                    configJsonCode
                                                                }
                                                                onChange={(
                                                                    code
                                                                ) => {
                                                                    setConfigJsonCode(
                                                                        code
                                                                    );
                                                                }}
                                                                readOnly={disabled}
                                                                editable={!disabled}
                                                                height="calc(100vh - 500px)"
                                                                width="100%"
                                                                lineWrapping={true}
                                                                lineNumbers={true}
                                                                extensions={[
                                                                    EditorView.lineWrapping,
                                                                ]}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* JSON Schema Tab */}
                                                <div
                                                    id="template-json-schema"
                                                    className="border border-border rounded-xl overflow-hidden bg-white shadow-xs transition-all mb-2.5 last:mb-0"
                                                >
                                                    <button
                                                        type="button"
                                                        className={`flex items-center justify-between w-full px-3.5 py-2.5 text-xs font-semibold transition-colors duration-150 ${
                                                            codeType === "json_schema"
                                                                ? "bg-primary-soft/50 text-primary border-b border-border"
                                                                : "bg-white text-stride-heading hover:bg-surface-subtle"
                                                        }`}
                                                        onClick={() => {
                                                            setCodeType((prev) =>
                                                                prev !== "json_schema" ? "json_schema" : ""
                                                            );
                                                        }}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <FileJsonIcon className="w-4 h-4 text-primary shrink-0" />
                                                            <span>
                                                                {
                                                                    content
                                                                        .EDIT_EXPERIENCE
                                                                        .json_schema_tab
                                                                }
                                                            </span>
                                                            <span className="text-[10px] font-mono font-medium px-1.5 py-0.5 rounded bg-surface border border-border text-stride-muted uppercase">
                                                                SCHEMA
                                                            </span>
                                                        </span>
                                                        <ChevronDown
                                                            className={`w-4 h-4 transition-transform duration-200 shrink-0 ${
                                                                codeType === "json_schema"
                                                                    ? "rotate-180 text-primary"
                                                                    : "text-stride-muted"
                                                            }`}
                                                        />
                                                    </button>
                                                    {codeType === "json_schema" && (
                                                        <div className="overflow-auto text-wrap">
                                                            <CodeMirror
                                                                value={jsonSchemaCode
                                                                    .replaceAll(
                                                                        "&lt;",
                                                                        "<"
                                                                    )
                                                                    ?.replaceAll(
                                                                        "&gt;",
                                                                        ">"
                                                                    )}
                                                                onChange={(
                                                                    code
                                                                ) => {
                                                                    setJsonSchemaCode(
                                                                        code
                                                                            .replaceAll(
                                                                                "&lt;",
                                                                                "<"
                                                                            )
                                                                            ?.replaceAll(
                                                                                "&gt;",
                                                                                ">"
                                                                            )
                                                                    );
                                                                }}
                                                                readOnly={
                                                                    disabled ||
                                                                    templateSource ==
                                                                        enums
                                                                            ?.templateSources
                                                                            .api
                                                                            .id
                                                                }
                                                                editable={
                                                                    !(
                                                                        disabled ||
                                                                        templateSource ==
                                                                            enums
                                                                                ?.templateSources
                                                                                .api
                                                                                .id
                                                                    )
                                                                }
                                                                height="calc(100vh - 500px)"
                                                                width="100%"
                                                                lineWrapping={
                                                                    true
                                                                }
                                                                lineNumbers={
                                                                    true
                                                                }
                                                                extensions={[
                                                                    EditorView.lineWrapping,
                                                                ]}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <p className="mt-2 text-sm text-red-5">
                                            {error}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </ResizablePanel>
                    )}

                    {(!previewOnly && templateType !== enums?.templateTypes.no_preview.id) && (
                            <ResizableHandle withHandle={true} />
                        )}

                    {(previewOnly || templateType !== enums?.templateTypes.no_preview.id) && (
                        <ResizablePanel defaultSize={70} minSize={40}>
                            <div className="w-full h-full">
                                <DeviceEmulator
                                    isLandscapeSupport={isLandscapeSupport}
                                    platformType={templatePlatform}
                                    isPreviewTitleBarEnable={
                                        isPreviewTitleBarEnable
                                    }
                                    title={title}
                                    handleBackwardArrowClick={
                                        handleBackwardArrowClick
                                    }
                                    currentIndex={currentIndex}
                                    handleForwardArrowClick={
                                        handleForwardArrowClick
                                    }
                                    handleProcessedClick={handleProcessedClick}
                                    handleCancelClick={handleCancelClick}
                                    onChange={(event) => {
                                        console.log(event)
                                        const newLandscape = !!event?.landscape;
                                        const newViewBg =
                                            !!event?.viewBackgroundImage;

                                        const shouldUpdateLandscape =
                                            landscapeRef.current !==
                                            newLandscape;
                                        const shouldUpdateBgImage =
                                            viewBackgroundImageRef.current !==
                                            newViewBg;

                                        if (
                                            shouldUpdateLandscape ||
                                            shouldUpdateBgImage
                                        ) {
                                            landscapeRef.current = newLandscape;
                                            viewBackgroundImageRef.current =
                                                newViewBg;
                                            handleTemplateChanges(
                                                htmlCode,
                                                cssCode,
                                                configJsonCode,
                                                jsonSchemaCode,
                                                templateName,
                                                templateViewURL,
                                                templateType,
                                                templatePlatform,
                                                templateSource,
                                                disabled,
                                                enums,
                                                checkPermission,
                                                data
                                            );
                                        }
                                    }}
                                >
                                    {(props) => (
                                        templateType === enums?.templateTypes.no_preview.id ? (
                                            <div className="flex items-center justify-center h-full w-full bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6">
                                                <p className="text-gray-500 font-medium text-lg">No visual preview available for this template type.</p>
                                            </div>
                                        ) : (
                                            <DeviceFrameset {...props}>
                                                <HtmlViewerWithInspector
                                                    htmlCode={htmlCode || selectedTemplate?.html || ""}
                                                    cssCode={cssCode || selectedTemplate?.css || ""}
                                                    experience_json={getExperienceJSON(isValidJsonString(configJsonCode || selectedTemplate?.config_json) ? JSON.parse(configJsonCode || selectedTemplate?.config_json) : [])}
                                                    configJsonData={isValidJsonString(configJsonCode || selectedTemplate?.config_json) ? JSON.parse(configJsonCode || selectedTemplate?.config_json) : []}
                                                    configFieldNameMain={isValidJsonString(jsonSchemaCode) ? JSON.parse(jsonSchemaCode).config_obj_key : null}
                                                    inspectorEnabled={inspectorEnabled}
                                                    onElementUpdate={handleElementUpdate}
                                                    web_url={viewBackgroundImageRef.current && getViewUrl(landscapeRef.current, selectedTemplate?.view_url) ? getViewUrl(landscapeRef.current, selectedTemplate?.view_url) : ""}
                                                    templateType={templateType}
                                                />
                                            </DeviceFrameset>
                                        )
                                    )}
                                </DeviceEmulator>
                            </div>
                        </ResizablePanel>
                    )}
                </ResizablePanelGroup>
            </div>

            {openNameModal && (
                <TemplateNameModal
                    hideModal={() => setOpenNameModal(false)}
                    initialName={templateName}
                    setName={setTemplateName}
                    onSubmit={() => {
                        saveTemplateHandler();
                        setOpenNameModal(false);
                    }}
                    loading={loading}
                    error={templateError || submitError}
                />
            )}
            <ImportFromFigmaModal
                showModal={openImportFromFigmaModal}
                handleCloseModal={() => setOpenImportFromFigmaModal(false)}
                handleImportSuccess={(data) => {
                    setOpenImportFromFigmaModal(false);
                    setHtmlCode(data?.html_code || "");
                    setCssCode(data?.css_code || "");
                    if (data?.asset_count && data?.asset_count > 0) {
                        toast.warn(
                            `Assets found in the Figma file. Please import them manually.`
                        );
                    }
                }}
            />
        </div>
    );
};

export default PreviewTemplate;
